import {
  API,
  type CCConversationDetailResponse,
  type CCConversationListItem,
  type CCMessageItem,
  type CCMissedOffer,
  type CCUserDetailResponse
} from "@swivl-mono/api-helper";
import {
  type StreamStatusUpdate,
  confirmOptimistic,
  failOptimistic,
  ingestMessage,
  ingestStatusUpdates,
  mergeFetchedMessages
} from "@swivl-mono/chat-ui";
import { warn } from "@swivl-mono/helpers";
import type { AgentOutboundTextResult, CCVoiceToken, CallScriptAgentView } from "@swivl-mono/types";
import {
  type CoachingNudgeInterface,
  type CoachingScoreSnapshotInterface,
  ConversationFocusSource,
  type ConversationMessage,
  type DebugLevel,
  UserVarKey,
  debugLevelAtLeast
} from "@swivl-mono/types";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";
import BotPresenceManager from "./BotPresence.Manager";

// ─── State ─────────────────────────────────────────────────────────────────

export type CCActiveTab = "conversations" | "agent-dashboard" | "recordings";
/** Right column tabs (ENG-2957): AI Assistance appears only post-accept; details live in cards above the tabs. */
export type CCRightPanelTab = "assist" | "notes" | "history";

/**
 * The server treats `User:IsBlocked` as a truthy STRING check, so anything
 * non-empty blocks. Mirrored here so the menu's state matches what the server
 * will actually enforce, including legacy values written before ENG-3191.
 */
function isUserBlockedValue(value?: string): boolean {
  return Boolean(value);
}

/** The Outbound Gate's reason for a simulated agent text, in the agent's words (ENG-3592). */
export function simulatedTextReason(reason?: string): string {
  if (reason === "flag_off") return "Texts are turned off for this org.";
  if (reason === "not_listed") return "This number isn't on the test allowlist.";
  if (reason === "allowlist_not_loaded") return "The test allowlist hasn't loaded yet. Try again in a minute.";
  if (reason === "no_recipient") return "There's no number to text for this customer.";
  return reason ? `(${reason})` : "";
}

/**
 * Conversation detail as held in the store: same shape the API returns except
 * `messages` are the canonical {@link ConversationMessage} (mapped once at the
 * fetch boundary) so the shared MessageStream core operates on them directly
 * (ENG-2825) and the timeline renders them with no per-render mapping.
 */
export type CCChannelDetail = Omit<CCConversationDetailResponse["channels"][number], "messages"> & { messages: ConversationMessage[] };
export type CCConversationDetail = Omit<CCConversationDetailResponse, "channels"> & { channels: CCChannelDetail[] };

/** Map one CC API row to the canonical render/stream shape — the fetch-boundary map (message-flow.md §1.1). */
export function mapCCMessage(m: CCMessageItem, botConversationId: number): ConversationMessage {
  return {
    messageId: m.messageId,
    messageIndex: m.messageIndex,
    role: m.role as ConversationMessage["role"],
    content: m.content,
    createdAt: m.timestamp,
    id: m.id,
    type: m.type,
    visible: m.visible,
    metadata: m.metadata,
    botLLMCallId: m.botLLMCallId,
    channel: m.channel as ConversationMessage["channel"],
    botConversationId,
    deliveryStatus: m.deliveryStatus,
    deliveryReason: m.deliveryReason,
    seenBy: m.seenBy,
    contentType: m.contentType as ConversationMessage["contentType"],
    payload: m.payload,
    actionTaken: m.actionTaken
  };
}

interface StoreInterface {
  // Conversation list
  conversations: CCConversationListItem[];
  myEscalations: CCConversationListItem[];
  isLoadingConversations: boolean;
  /**
   * The Open list has answered at least once (rows, none, or an error) for the current org. The
   * page's full-panel spinner shows only before this (ENG-3809): a later reload with an empty list —
   * a search that matches nothing, then any socket resync — must never unmount the list, or the
   * search box collapses mid-type while the filter it typed keeps constraining every reload.
   */
  hasLoadedConversations: boolean;
  /**
   * The active server-side search (ENG-2791). In state, not a static, so the box and the filter
   * can never disagree: a remounted list reads its text back from here (ENG-3809).
   */
  listSearch: string;
  /** Last list fetch errored — the list must say so instead of posing as "no results". */
  listLoadFailed: boolean;
  /** Which list the sidebar shows (ENG-2791 design: Open | Closed tabs). */
  listTab: "open" | "closed";
  /** Agent-closed conversations — populated when the Closed tab is active. */
  closedConversations: CCConversationListItem[];

  // Selected user/conversation
  selectedUserId: number | null;
  selectedChannel: string | null;
  /**
   * A ticket's "View conversation" deep-link target (ENG-2929 C4): the ONE
   * botConversationId the page should land on. One-shot — cleared as soon as
   * it's applied (live conversation: on channel select; previous
   * conversation: after the timeline auto-expands its card).
   */
  deepLinkConversationId: number | null;

  // Conversation detail
  conversationDetail: CCConversationDetail | null;
  isLoadingDetail: boolean;

  // Message visibility level — see docs/snowy/plans/2026-04-28-buttons-kind-plan.md §0.4
  messageVisibility: DebugLevel;

  /**
   * Per-conversation read watermark AS OF OPENING (from the detail response,
   * captured before the open stamps a new one) — anchors the "New Messages"
   * divider and stays put while the conversation is viewed. Cleared on
   * conversation switch.
   */
  newMessagesWatermarks: Record<number, number>;

  // Active tab
  activeTab: CCActiveTab;

  // Right panel tab
  rightPanelTab: CCRightPanelTab;

  // Agent message input
  messageInput: string;
  isSendingMessage: boolean;

  /** Tracks the highest known messageIndex per botConversationId for incremental fetching */
  lastKnownMessageIndexes: Record<number, number>;
  /** Last messageIndex the agent viewed per botConversationId — for unread badge calculation */
  lastReadMessageIndexes: Record<number, number>;
  /** message-status updates that raced ahead of their message, per botConversationId (R8) — re-applied on arrival */
  bufferedStatusUpdates: Record<number, StreamStatusUpdate[]>;

  /** Per-user-per-channel online status. Key: "userId:channel" → boolean */
  userOnlineStatus: Record<string, boolean>;

  /** Per-user-per-channel "actively viewing the chat" (panel open). Key: "userId:channel" → boolean.
   *  online + viewing → chatting (teal); online + !viewing → connected-but-closed (orange). */
  userViewingStatus: Record<string, boolean>;

  /** Agent presence. Key: memberId → { state, activeAssignmentCount } */
  agentPresence: Record<number, { state: string; activeAssignmentCount: number }>;

  // ─── Coaching (ENG-2957) — server-driven; reload state via GET, live via coaching:* sockets ───

  /** Latest persisted score snapshot for the selected conversation (score ring + tip + running summary). */
  coachingSnapshot: CoachingScoreSnapshotInterface | null;
  /**
   * Score trend for the selected conversation (ENG-3068): signed points per
   * message of the last real movement. Direction drives the arrow, magnitude
   * its colour. Lives on the conversation (not the snapshot) so it survives
   * ticks where the score held steady.
   */
  coachingScoreVelocity: number | null;
  /** All nudges for the selected conversation (every lifecycle state — the panel filters). */
  coachingNudges: CoachingNudgeInterface[];
  /** Accumulated conversation topics (append-only; header chips). */
  coachingTopics: string[];
  /** The script in play as agents see it (ENG-3402); null until the engine picks one. */
  coachingScript: CallScriptAgentView | null;
  /** Which conversation the coaching slice belongs to (guards stale socket events). */
  coachingConversationId: number | null;
  isLoadingCoaching: boolean;
  /** The agent's working context (§13.5): what the conversation is focused on. */
  coachingFocus: {
    locationId: number | null;
    locationName: string | null;
    unitId: number | null;
    unitName: string | null;
    /** Who set it (ENG-3186) — labels the Location in Focus card. */
    source: ConversationFocusSource | null;
  } | null;
  /** Nudge ids arrived over the socket while the agent was scrolled away ("New live nudge" pill). */
  unseenNudgeIds: number[];

  /** Snowy-native user detail for the right column cards (header, facility, history). */
  ccUserDetail: CCUserDetailResponse | null;
  isLoadingUserDetail: boolean;
  /** Facility JIT refresh in flight (freshness spinner on the facility card). */
  isRefreshingFacility: boolean;
  /** When the facility data was last refreshed/synced (ISO) — the "as of" chip. */
  facilityDataAsOf: string | null;

  // Voice state
  /** The first softphone token — kept for the voice action calls, which only need the identity. */
  twilioToken: string | null;
  twilioIdentity: string | null;
  /** One token per Twilio account the org's numbers live on (ENG-3656); the softphone registers on each. */
  twilioTokens: CCVoiceToken[] | null;
  activeCallSid: string | null;
  voiceState: "idle" | "listening" | "active" | "dialing";
  isMuted: boolean;
  /** Microphone the CC is using — null until the Device has chosen one. */
  audioInputDeviceId: string | null;
  /** Live 0..1 peak from that microphone while the meter runs. */
  audioInputLevel: number;
  /** Set when the selected microphone has been silent during a live call. */
  audioInputDead: boolean;

  // Error banner

  // Assignment toast
  pendingAssignmentToast: {
    assignmentId: number;
    userName: string;
    channel: string;
    reason?: string;
    /** Server-authoritative deadline (ISO) — countdown renders from this, never a local timer (ENG-2826). */
    expiresAt?: string;
    /** Lets Accept navigate straight to the accepted conversation. */
    userId?: number;
    /** The escalated conversation — a voice Accept bridges to it directly (ENG-3408). */
    botConversationId?: number;
  } | null;

  /** Offers that expired while the agent wasn't looking — "You missed this one" notices (ENG-2826). */
  missedOfferNotices: CCMissedOffer[];
  /**
   * "Your manager set your status to Offline." — rides the same bottom-right CC
   * stack as offers, because it is the same class of thing: an alert to THIS
   * agent about their own work state (ENG-3098). Null when there is nothing to
   * show.
   */
  presenceNotice: string | null;
  /**
   * Transient confirmations/errors for CC actions ("X set offline",
   * "Offered to Y", failures). Rendered in the SAME bottom-right stack —
   * never react-toastify's top-right container (Chuck, 2026-08-24: CC surfaces
   * always use the CC toasts). Sticky with a Dismiss, per the no-auto-dismiss
   * rule.
   */
  ccNotices: Array<{ id: number; kind: "success" | "error"; text: string }>;
}

/** How long a CC toast stays up: react-toastify errors, and success notices in the CC stack. */
const CC_TOAST_DURATION_MS = 5000;

/** Persisted debug-level selection (ENG-2946) — survives sessions. */
const DEBUG_LEVEL_STORAGE_KEY = "cc-v3-debug-level";

const BlankState: StoreInterface = {
  conversations: [],
  myEscalations: [],
  isLoadingConversations: false,
  hasLoadedConversations: false,
  listSearch: "",
  listLoadFailed: false,
  listTab: "open",
  closedConversations: [],

  selectedUserId: null,
  selectedChannel: null,
  deepLinkConversationId: null,

  conversationDetail: null,
  isLoadingDetail: false,
  messageVisibility: "agent",
  newMessagesWatermarks: {},

  activeTab: "conversations",
  rightPanelTab: "assist",

  messageInput: "",
  isSendingMessage: false,

  lastKnownMessageIndexes: {},
  lastReadMessageIndexes: {},
  bufferedStatusUpdates: {},

  userOnlineStatus: {},
  userViewingStatus: {},
  agentPresence: {},

  coachingSnapshot: null,
  coachingScoreVelocity: null,
  coachingNudges: [],
  coachingTopics: [],
  coachingScript: null,
  coachingConversationId: null,
  isLoadingCoaching: false,
  coachingFocus: null,
  unseenNudgeIds: [],

  ccUserDetail: null,
  isLoadingUserDetail: false,
  isRefreshingFacility: false,
  facilityDataAsOf: null,

  twilioToken: null,
  twilioIdentity: null,
  twilioTokens: null,
  activeCallSid: null,
  voiceState: "idle",
  isMuted: false,
  audioInputDeviceId: null,
  audioInputLevel: 0,
  audioInputDead: false,

  pendingAssignmentToast: null,
  missedOfferNotices: [],
  presenceNotice: null,
  ccNotices: []
};

// ─── Model ─────────────────────────────────────────────────────────────────

export default class ContactCenterV3Model {
  static useState = create<StoreInterface>(() => ({ ...BlankState }));
  static setState = ContactCenterV3Model.useState.setState;

  static get state(): StoreInterface {
    return ContactCenterV3Model.useState.getState();
  }

  static reset(): void {
    ContactCenterV3Model.setState({ ...BlankState });
  }

  // ─── Conversation List ─────────────────────────────────────────────────

  /**
   * Monotonic guard against out-of-order list responses: a slow search
   * response must never clobber the result of a later fetch (e.g. the
   * cleared-search reload) — the list would silently stick on stale rows.
   */
  private static listRequestSeq = 0;

  /** The org the Open list last loaded for: a load for another org starts from a blank search (ENG-3809). */
  private static listOrgId: number | null = null;

  /** The active server-side search (ENG-2791); reloads reuse it so a resync doesn't wipe search results. */
  static setListSearch(query: string): void {
    ContactCenterV3Model.setState({ listSearch: query });
    if (ContactCenterV3Model.state.listTab === "closed") void ContactCenterV3Model.loadClosedConversations();
    else void ContactCenterV3Model.loadConversations();
  }

  /**
   * Forget the search without a fetch — on leaving the page (ENG-3809). A search left behind
   * would silently constrain the next visit's list with no box showing it.
   */
  static clearListSearch(): void {
    ContactCenterV3Model.setState({ listSearch: "" });
  }

  static async loadConversations(orgId?: number, memberId?: number): Promise<void> {
    const org = orgId || Member.orgId;
    const member = memberId || Member.id;
    if (!org || !member) return;

    if (ContactCenterV3Model.listOrgId !== org) {
      // Another org: its list has not loaded yet, and the previous org's search does not carry over.
      ContactCenterV3Model.listOrgId = org;
      ContactCenterV3Model.setState({ hasLoadedConversations: false, listSearch: "" });
    }
    ContactCenterV3Model.setState({ isLoadingConversations: true });
    const seq = ++ContactCenterV3Model.listRequestSeq;
    try {
      const response = await API.ContactCenterV3.getConversations(org, member, undefined, ContactCenterV3Model.state.listSearch || undefined);
      if (seq !== ContactCenterV3Model.listRequestSeq) return; // a newer fetch superseded this one

      // Build online + viewing status maps from conversation channel data so the
      // presence dot is correct on load (teal when the user is actively viewing),
      // not only after the next live status_changed event.
      const onlineStatus: Record<string, boolean> = { ...ContactCenterV3Model.state.userOnlineStatus };
      const viewingStatus: Record<string, boolean> = { ...ContactCenterV3Model.state.userViewingStatus };
      for (const item of [...response.myEscalations, ...response.otherConversations]) {
        for (const ch of item.channels) {
          onlineStatus[`${item.userId}:${ch.channel}`] = ch.userOnline;
          viewingStatus[`${item.userId}:${ch.channel}`] = ch.userViewing ?? false;
        }
      }

      ContactCenterV3Model.setState({
        myEscalations: response.myEscalations,
        conversations: response.otherConversations,
        isLoadingConversations: false,
        hasLoadedConversations: true,
        listLoadFailed: false,
        userOnlineStatus: onlineStatus,
        userViewingStatus: viewingStatus
      });
    } catch (e) {
      warn("ContactCenterV3Model.loadConversations failed", e);
      if (seq !== ContactCenterV3Model.listRequestSeq) return;
      ContactCenterV3Model.setState({ isLoadingConversations: false, hasLoadedConversations: true, listLoadFailed: true });
    }
  }

  // ─── User Selection ────────────────────────────────────────────────────

  static selectUser(userId: number | null): void {
    // Stop watching the previously-open conversation's rooms before switching.
    const prev = ContactCenterV3Model.state.conversationDetail;
    if (prev) {
      for (const ch of prev.channels) BotPresenceManager.unwatchConversation(ch.botConversationId);
    }
    ContactCenterV3Model.setState({
      selectedUserId: userId,
      conversationDetail: null,
      selectedChannel: null,
      messageInput: "",
      newMessagesWatermarks: {},
      // Right column resets with the selection (ENG-2957 QA #2). These used to
      // wait for the next fetch to overwrite them, so a user whose detail
      // resolves to nothing (e.g. soft-deleted) kept the PREVIOUS customer's
      // name, tickets, score and nudge history on screen under the new URL.
      ccUserDetail: null,
      isLoadingUserDetail: false,
      coachingConversationId: null,
      coachingSnapshot: null,
      coachingScoreVelocity: null,
      coachingNudges: [],
      coachingFocus: null,
      coachingTopics: [],
      unseenNudgeIds: [],
      isLoadingCoaching: false
    });
  }

  /**
   * ENG-2929 C4 — set (or clear) the ticket deep-link target. Applied
   * immediately when the target user's detail is already on screen;
   * otherwise `loadConversationDetail` applies it when the detail lands.
   */
  static setDeepLinkConversation(id: number | null): void {
    ContactCenterV3Model.setState({ deepLinkConversationId: id });
    if (id) ContactCenterV3Model.applyDeepLinkChannelSelection();
  }

  /**
   * History tab "View Conversation" (ENG-2957 QA #10): land on one of this
   * customer's conversations. When it is already on screen — the live thread
   * on some channel, or a card in a channel's previous stack — resolve it
   * directly and let the timeline expand + scroll to it. NO refetch in that
   * case: a refetch replaces `messages`, and the shared timeline's
   * scroll-to-bottom then yanks the view away from the card it just scrolled
   * to. Only a conversation older than the recency window needs the refetch,
   * which carries the id so the server includes it; `loadConversationDetail`
   * applies the deep link when the response lands.
   */
  static viewConversation(botConversationId: number): void {
    const detail = ContactCenterV3Model.state.conversationDetail;
    const isLoaded =
      detail?.channels.some(
        (c) => c.botConversationId === botConversationId || c.previousConversations?.some((p) => p.botConversationId === botConversationId)
      ) ?? false;
    if (isLoaded) {
      ContactCenterV3Model.setDeepLinkConversation(botConversationId);
      return;
    }
    ContactCenterV3Model.setState({ deepLinkConversationId: botConversationId });
    void ContactCenterV3Model.loadConversationDetail(undefined, undefined, undefined, { silent: true });
  }

  /**
   * Land on the deep-linked conversation: a LIVE channel conversation is
   * selected directly (and the deep-link clears — done); a PREVIOUS
   * conversation selects its channel and keeps the id set so the timeline
   * auto-expands that card (which clears it). An id matching nothing clears
   * so a stale link can't haunt later navigation.
   */
  private static applyDeepLinkChannelSelection(): void {
    const { deepLinkConversationId, conversationDetail } = ContactCenterV3Model.state;
    if (!deepLinkConversationId || !conversationDetail) return;
    const live = conversationDetail.channels.find((c) => c.botConversationId === deepLinkConversationId);
    if (live) {
      ContactCenterV3Model.setState({ selectedChannel: live.channel, deepLinkConversationId: null });
      return;
    }
    const prev = conversationDetail.channels.find((c) => c.previousConversations?.some((p) => p.botConversationId === deepLinkConversationId));
    if (prev) {
      ContactCenterV3Model.setState({ selectedChannel: prev.channel });
      return;
    }
    ContactCenterV3Model.setState({ deepLinkConversationId: null });
  }

  // ─── Conversation Detail ───────────────────────────────────────────────

  static async loadConversationDetail(orgId?: number, userId?: number, channel?: string, opts?: { silent?: boolean }): Promise<void> {
    const org = orgId || Member.orgId;
    const user = userId || ContactCenterV3Model.state.selectedUserId;
    if (!org || !user) return;

    // Silent refresh (ENG-2946): re-fetch WITHOUT flipping isLoadingDetail —
    // the loading flag unmounts the whole center column into a spinner, which
    // is only acceptable when SWITCHING users. Event-driven refreshes
    // (assignment/escalation changes, own actions) update in place. First
    // load of a user (no detail yet, or a different user's detail) always
    // shows the spinner regardless.
    const hasCurrentDetail = ContactCenterV3Model.state.conversationDetail?.userId === user;
    const silent = (opts?.silent ?? false) && hasCurrentDetail;
    if (!silent) ContactCenterV3Model.setState({ isLoadingDetail: true });
    try {
      const visibility = ContactCenterV3Model.state.messageVisibility;
      // Deep-link target rides along so the server includes it in the previous
      // stack even when it's older than the recency window (ENG-2929 C4).
      const response = await API.ContactCenterV3.getConversationDetail(
        org,
        user,
        channel,
        visibility,
        ContactCenterV3Model.state.deepLinkConversationId ?? undefined
      );

      // Map API rows → canonical ConversationMessage at the fetch boundary.
      const detail: CCConversationDetail = {
        ...response,
        channels: response.channels.map((ch) => ({ ...ch, messages: ch.messages.map((m) => mapCCMessage(m, ch.botConversationId)) }))
      };

      // Track the highest messageIndex per channel for incremental fetching
      const messageIndexes = { ...ContactCenterV3Model.state.lastKnownMessageIndexes };
      for (const ch of detail.channels) {
        if (ch.messages.length > 0) {
          const maxMsgIdx = Math.max(...ch.messages.map((m) => m.messageIndex));
          messageIndexes[ch.botConversationId] = maxMsgIdx;
        }
      }

      // Snapshot the pre-open read watermark once per viewed conversation —
      // later detail refreshes (escalation changes) must not move the
      // "New Messages" divider mid-view.
      const watermarks = { ...ContactCenterV3Model.state.newMessagesWatermarks };
      for (const ch of response.channels) {
        if (ch.memberLastReadIndex != null && watermarks[ch.botConversationId] === undefined) {
          watermarks[ch.botConversationId] = ch.memberLastReadIndex;
        }
      }

      ContactCenterV3Model.setState({
        conversationDetail: detail,
        isLoadingDetail: false,
        lastKnownMessageIndexes: messageIndexes,
        newMessagesWatermarks: watermarks
      });

      // Watch each channel's conversation room over the bot socket so this
      // conversation's live traffic reaches this agent (room emits land in
      // Phase 3). Idempotent; the bot authorizes org ownership before joining.
      for (const ch of response.channels) BotPresenceManager.watchConversation(ch.botConversationId);

      // Deep-linked conversation (ENG-2929 C4) wins channel selection.
      ContactCenterV3Model.applyDeepLinkChannelSelection();

      // Auto-select first channel if none selected
      if (!ContactCenterV3Model.state.selectedChannel && response.channels.length > 0) {
        ContactCenterV3Model.setState({ selectedChannel: response.channels[0].channel });
      }

      // Local "New Messages" divider watermarks for all loaded channels.
      const readSeqs = { ...ContactCenterV3Model.state.lastReadMessageIndexes };
      for (const ch of response.channels) {
        const msgIdx = messageIndexes[ch.botConversationId];
        if (msgIdx != null) readSeqs[ch.botConversationId] = msgIdx;
      }
      ContactCenterV3Model.setState({ lastReadMessageIndexes: readSeqs });

      // Read receipt — stamp seenBy.members + the inbox watermark, and push it
      // live to the user's widget (its ticks advance to "seen"). ONLY the
      // channel actually on screen (ENG-2946 badge regression): stamping every
      // channel/conversation of the user here bulk-nuked unread across rows
      // the agent never looked at (proven in dev: five conversations
      // watermarked in the same millisecond), so badges never survived.
      // Channel-tab switches stamp their own conversation in
      // setSelectedChannel.
      const viewedChannel = ContactCenterV3Model.state.selectedChannel ?? response.channels[0]?.channel;
      const viewed = response.channels.find((c) => c.channel === viewedChannel);
      if (!document.hidden && viewed) {
        BotPresenceManager.markRead(viewed.botConversationId);
        ContactCenterV3Model.clearListUnread(viewed.botConversationId);
      }

      // Right column data (ENG-2957): user detail feeds the header/facility/
      // history cards. Coaching state loads for EVERY conversation (bot-phase
      // scores are product-visible, Chuck 2026-08-14) — the escalated channel
      // wins when present, and escalation auto-selects the AI Assistance tab.
      void ContactCenterV3Model.loadUserDetail(user);
      const escalatedChannel = response.channels.find((c) => c.escalationStatus === "active");
      const coachingTarget = escalatedChannel ?? response.channels[0];
      if (coachingTarget && ContactCenterV3Model.state.coachingConversationId !== coachingTarget.botConversationId) {
        void ContactCenterV3Model.loadCoachingState(coachingTarget.botConversationId);
      }
      if (escalatedChannel && ContactCenterV3Model.state.rightPanelTab !== "assist") {
        ContactCenterV3Model.setState({ rightPanelTab: "assist" });
      }
    } catch (e) {
      warn("ContactCenterV3Model.loadConversationDetail failed", e);
      ContactCenterV3Model.setState({ isLoadingDetail: false });
    }
  }

  // ─── Agent Messages ────────────────────────────────────────────────────

  static setMessageInput(value: string): void {
    ContactCenterV3Model.setState({ messageInput: value });
  }

  static async sendMessage(attachment?: { imageUrl: string }): Promise<void> {
    const { conversationDetail, selectedChannel, messageInput } = ContactCenterV3Model.state;
    if (!conversationDetail || !selectedChannel || (!messageInput.trim() && !attachment)) return;

    const memberId = Member.id;
    if (!memberId) return;

    // Agent interaction → reset the idle timer (keeps presence out of `away`).
    BotPresenceManager.recordActivity();

    const channelData = conversationDetail.channels.find((c) => c.channel === selectedChannel);
    if (!channelData) return;

    // An attachment sends as its own image message — it must not swallow
    // whatever the agent has typed in the composer.
    const content = attachment ? "" : messageInput.trim();

    // Client-generated uuid — the canonical messageId AND the idempotency key
    // (ENG-2825): the optimistic copy, the saved row, the room echo, and any
    // catch-up fetch all carry it, so reconciliation is exact (never by content).
    const messageId = uuid();

    const optimisticMessage: ConversationMessage = {
      messageId,
      messageIndex: -1, // allocated by the server
      role: "assistant",
      author: "agent",
      agentName: Member.displayName ?? undefined,
      content,
      channel: selectedChannel as ConversationMessage["channel"],
      botConversationId: channelData.botConversationId,
      createdAt: new Date().toISOString(),
      visible: true,
      type: "message",
      deliveryStatus: "pending",
      // An image attachment is a structured `image` message (ENG-2828) — the
      // optimistic copy carries the same payload the saved row will have.
      ...(attachment ? { contentType: "structured" as const, payload: { kind: "image", data: { url: attachment.imageUrl } } } : {}),
      metadata: { isAgentMessage: true, agentName: Member.displayName }
    };

    ContactCenterV3Model.setState({
      ...(attachment ? {} : { messageInput: "" }),
      isSendingMessage: true,
      conversationDetail: ContactCenterV3Model.withChannelMessages(selectedChannel, (msgs) => [...msgs, optimisticMessage])
    });

    try {
      const result = await API.ContactCenterV3.sendAgentMessage(
        channelData.botConversationId,
        memberId,
        content,
        selectedChannel,
        messageId,
        attachment?.imageUrl
      );

      const messageIndexes = { ...ContactCenterV3Model.state.lastKnownMessageIndexes };
      messageIndexes[channelData.botConversationId] = Math.max(messageIndexes[channelData.botConversationId] ?? -1, result.messageIndex);

      ContactCenterV3Model.setState({
        isSendingMessage: false,
        lastKnownMessageIndexes: messageIndexes,
        conversationDetail: ContactCenterV3Model.withChannelMessages(selectedChannel, (msgs) =>
          // Honest ticks (ENG-2946): server confirmation = SAVED, i.e. "sent"
          // (one grey tick). "delivered" arrives via the live message-status
          // event when the widget ACKs / the SMS webhook reports; "seen" only
          // when the user actually reads. Hardcoding "delivered" here showed
          // double ticks for sends into conversations no widget had open.
          // A text's server answer carries the carrier's verdict (ENG-3281):
          // simulated, blocked or failed shows its own mark at once.
          confirmOptimistic(msgs, messageId, { id: result.id, messageIndex: result.messageIndex, deliveryStatus: result.deliveryStatus ?? "sent" })
        )
      });
      // The bot's room echo of this message can land before the server answers and reconcile the
      // optimistic copy first, so apply the verdict by id too, not only through confirmOptimistic.
      if (result.deliveryStatus) {
        ContactCenterV3Model.ingestMessageStatus({
          conversationId: channelData.botConversationId,
          updates: [
            {
              messageId,
              messageIndex: result.messageIndex,
              botConversationId: channelData.botConversationId,
              deliveryStatus: result.deliveryStatus,
              ...(result.reason ? { deliveryReason: result.reason } : {})
            }
          ]
        });
      }
      ContactCenterV3Model.noticeForWithheldText(result.deliveryStatus, result.reason);
    } catch (e) {
      warn("ContactCenterV3Model.sendMessage failed", e);
      // Keep the copy visible in a failed state. If the send actually reached
      // the server (lost response), the room echo / catch-up fetch reconciles
      // it by messageId (R5) and the failed copy resolves to the real row.
      ContactCenterV3Model.setState({
        isSendingMessage: false,
        conversationDetail: ContactCenterV3Model.withChannelMessages(selectedChannel, (msgs) => failOptimistic(msgs, messageId))
      });
    }
  }

  /** Immutable update of one channel's message list inside conversationDetail (null when nothing is open). */
  private static withChannelMessages(channel: string, update: (msgs: ConversationMessage[]) => ConversationMessage[]): CCConversationDetail | null {
    const detail = ContactCenterV3Model.state.conversationDetail;
    if (!detail) return null;
    return { ...detail, channels: detail.channels.map((ch) => (ch.channel === channel ? { ...ch, messages: update(ch.messages) } : ch)) };
  }

  // ─── Incremental Message Fetching ───────────────────────────────────────

  /**
   * Fetch only new messages since the last known messageIndex for a given
   * botConversationId and merge them through the shared MessageStream core —
   * dedup by messageId/messageIndex, stuck pending sends reconcile by
   * messageId (R5). No full refetch.
   */
  static async fetchNewMessages(botConversationId: number): Promise<void> {
    const { conversationDetail, lastKnownMessageIndexes, messageVisibility } = ContactCenterV3Model.state;
    if (!conversationDetail) return;

    const afterMessageIndex = lastKnownMessageIndexes[botConversationId] ?? -1;

    try {
      const newMessages = await API.ContactCenterV3.getNewMessages(botConversationId, afterMessageIndex, messageVisibility);
      if (newMessages.length === 0) return;

      const detail = ContactCenterV3Model.state.conversationDetail;
      if (!detail) return;

      const channel = detail.channels.find((ch) => ch.botConversationId === botConversationId);
      if (!channel) return;

      const mapped = newMessages.map((m) => mapCCMessage(m, botConversationId));
      const { messages } = mergeFetchedMessages(channel.messages, mapped);

      const maxNewIdx = Math.max(...mapped.map((m) => m.messageIndex));
      const messageIndexes = { ...ContactCenterV3Model.state.lastKnownMessageIndexes };
      messageIndexes[botConversationId] = Math.max(messageIndexes[botConversationId] ?? -1, maxNewIdx);

      ContactCenterV3Model.setState({
        conversationDetail: ContactCenterV3Model.withChannelMessages(channel.channel, () => messages),
        lastKnownMessageIndexes: messageIndexes
      });

      // Anything buffered for this conversation may now have its message (R8).
      ContactCenterV3Model.applyBufferedStatus(botConversationId);
    } catch (e) {
      warn("ContactCenterV3Model.fetchNewMessages failed", e);
    }
  }

  /**
   * Ingest a canonical {@link ConversationMessage} pushed live over the bot
   * socket's `conv:{id}` room — no HTTP fetch. Runs through the shared
   * MessageStream core (ENG-2825): dedup by messageId/messageIndex, the echo
   * of this agent's own send reconciles its optimistic copy by messageId, and
   * a skipped-ahead messageIndex triggers the catch-up delta fetch (R2) so a
   * lost emit self-heals. Ignores messages for conversations other than the
   * one currently open.
   */
  static ingestRoomMessage(msg: ConversationMessage): void {
    const detail = ContactCenterV3Model.state.conversationDetail;
    if (!detail || msg.botConversationId == null) return;

    const targetId = msg.botConversationId;
    const channel = detail.channels.find((ch) => ch.botConversationId === targetId);
    if (!channel) return;

    const { messages, added, gapDetected } = ingestMessage(channel.messages, msg);
    if (messages === channel.messages) return;

    // On a gap, the fetch cursor must NOT advance past the hole — if it did and
    // the catch-up fetch failed, the missing messages would never be fetched.
    // The successful fetch advances the cursor itself.
    const messageIndexes = { ...ContactCenterV3Model.state.lastKnownMessageIndexes };
    if (!gapDetected) messageIndexes[targetId] = Math.max(messageIndexes[targetId] ?? -1, msg.messageIndex);
    ContactCenterV3Model.setState({
      conversationDetail: ContactCenterV3Model.withChannelMessages(channel.channel, () => messages),
      lastKnownMessageIndexes: messageIndexes
    });

    // R8: a status update may have raced ahead of this message.
    ContactCenterV3Model.applyBufferedStatus(targetId);

    // R2: the stream skipped ahead — fetch whatever was lost in between.
    if (gapDetected) void ContactCenterV3Model.fetchNewMessages(targetId);

    // The agent is viewing this conversation and a user message just landed —
    // mark it seen immediately so the user's widget ticks advance to "seen".
    if (added && msg.role === "user" && typeof document !== "undefined" && !document.hidden) {
      BotPresenceManager.markRead(targetId);
      ContactCenterV3Model.clearListUnread(targetId);
    }
  }

  /**
   * Ingest a delivery/seen update pushed live over the bot socket's `conv:{id}`
   * room. Applies through the shared core; updates whose message hasn't
   * arrived yet are buffered per conversation and re-applied on arrival (R8)
   * instead of being dropped. Ignores updates for conversations other than
   * the one currently open.
   */
  static ingestMessageStatus(payload: { conversationId: number; updates: StreamStatusUpdate[] }): void {
    const detail = ContactCenterV3Model.state.conversationDetail;
    if (!detail || payload.updates.length === 0) return;

    const channel = detail.channels.find((ch) => ch.botConversationId === payload.conversationId);
    if (!channel) return;

    const { messages, unmatched } = ingestStatusUpdates(channel.messages, payload.updates);

    const buffered = { ...ContactCenterV3Model.state.bufferedStatusUpdates };
    if (unmatched.length > 0) buffered[payload.conversationId] = [...(buffered[payload.conversationId] ?? []), ...unmatched];

    ContactCenterV3Model.setState({
      ...(messages !== channel.messages ? { conversationDetail: ContactCenterV3Model.withChannelMessages(channel.channel, () => messages) } : {}),
      bufferedStatusUpdates: buffered
    });
  }

  /** Re-apply buffered status updates (R8) after new messages land for a conversation. */
  private static applyBufferedStatus(botConversationId: number): void {
    const { bufferedStatusUpdates, conversationDetail } = ContactCenterV3Model.state;
    const pending = bufferedStatusUpdates[botConversationId];
    if (!pending || pending.length === 0 || !conversationDetail) return;

    const channel = conversationDetail.channels.find((ch) => ch.botConversationId === botConversationId);
    if (!channel) return;

    const { messages, unmatched } = ingestStatusUpdates(channel.messages, pending);
    const buffered = { ...bufferedStatusUpdates };
    if (unmatched.length > 0) buffered[botConversationId] = unmatched;
    else delete buffered[botConversationId];

    ContactCenterV3Model.setState({
      ...(messages !== channel.messages ? { conversationDetail: ContactCenterV3Model.withChannelMessages(channel.channel, () => messages) } : {}),
      bufferedStatusUpdates: buffered
    });
  }

  /**
   * Re-establish the open conversation after a bot-socket (re)connect: re-watch
   * each channel's conv room (watches are on-demand, not restored server-side on
   * reconnect) and catch up any messages missed while disconnected. No-op when
   * nothing is open.
   */
  static resyncOpenConversation(): void {
    const detail = ContactCenterV3Model.state.conversationDetail;
    if (!detail) return;
    for (const ch of detail.channels) {
      BotPresenceManager.watchConversation(ch.botConversationId);
      void ContactCenterV3Model.fetchNewMessages(ch.botConversationId);
    }
  }

  // ─── Assignment Actions ────────────────────────────────────────────────

  /**
   * `assignment` is what the caller knows about the offer (toast payload or the
   * timeline's active channel). A voice Accept bridges to THAT conversation
   * before anything reloads — the detail-based lookup below only sees the
   * conversation the agent happens to be viewing, so accepting a voice offer
   * from the toast while on another conversation never dialed the bridge and
   * left the caller on hold (ENG-3408).
   */
  static async acceptAssignment(assignmentId: number, assignment?: { channel?: string; botConversationId?: number }): Promise<void> {
    const memberId = Member.id;
    if (!memberId) return;

    try {
      await API.ContactCenterV3.acceptAssignment(assignmentId, memberId);
      // Immediate presence recompute → busy (the assignment row is now committed,
      // so the bot's heartbeat sees the new active count without the ~30s wait).
      BotPresenceManager.heartbeat();

      // Accepted by ANY path (toast button, the conversation's own Accept, voice
      // controls) → the offer toast for this assignment is done. Before ENG-3475
      // only the toast's own button cleared it, so accepting from the page the
      // push notification lands on left the toast ringing until its countdown.
      if (ContactCenterV3Model.state.pendingAssignmentToast?.assignmentId === assignmentId) {
        ContactCenterV3Model.dismissAssignmentToast();
      }

      // For voice escalations: auto-bridge the agent into the bot's conference.
      // Pre-2026-06-09 the agent had to click Accept AND THEN click "Join Call"
      // in VoiceControls — two clicks for the same intent, and the bot was
      // sitting in hold waiting for a bridge that might never come. Tying the
      // bridge directly to Accept collapses to a single action.
      const bridgedConversationId = assignment?.channel === "voice" && assignment.botConversationId ? assignment.botConversationId : null;
      if (bridgedConversationId) {
        await ContactCenterV3Model.bridgeToAgent(bridgedConversationId);
      }

      // Switch to the AI Assistance tab and reload data
      ContactCenterV3Model.setState({ rightPanelTab: "assist" });
      await ContactCenterV3Model.loadConversations();
      await ContactCenterV3Model.loadConversationDetail(undefined, undefined, undefined, { silent: true });

      // Callers that don't know the assignment's conversation: bridge from the
      // loaded detail, which is the accepted conversation when Accept came from
      // its own timeline.
      const detail = ContactCenterV3Model.state.conversationDetail;
      const voiceChannel = detail?.channels.find((c) => c.channel === "voice" && c.escalationStatus === "active");
      if (!bridgedConversationId && voiceChannel) {
        await ContactCenterV3Model.bridgeToAgent(voiceChannel.botConversationId);
      }

      // Land on the accepted escalation's own channel (ENG-3320): Accept never set
      // the tab, so accepting a voice offer while viewing the user's SMS left the
      // SMS composer open under the voice bar. Only when the loaded detail is this
      // assignment's user — accepting from the toast on another user's page
      // navigates there and the deep link selects the channel.
      const escalatedChannel = detail?.channels.find((c) => c.escalationStatus === "active");
      const acceptedChannel = assignment?.channel ?? escalatedChannel?.channel;
      const detailIsThisUser =
        !assignment?.botConversationId || (detail?.channels.some((c) => c.botConversationId === assignment.botConversationId) ?? false);
      if (acceptedChannel && detailIsThisUser && ContactCenterV3Model.state.selectedChannel !== acceptedChannel) {
        ContactCenterV3Model.setSelectedChannel(acceptedChannel);
      }

      // Load coaching state + fire the accept-time facility JIT refresh (plan §9.5).
      if (escalatedChannel) {
        void ContactCenterV3Model.loadCoachingState(escalatedChannel.botConversationId);
        void ContactCenterV3Model.refreshFacility(escalatedChannel.botConversationId);
      }
    } catch (e) {
      warn("ContactCenterV3Model.acceptAssignment failed", e);
    }
  }

  static async rejectAssignment(assignmentId: number): Promise<void> {
    const memberId = Member.id;
    if (!memberId) return;

    try {
      await API.ContactCenterV3.rejectAssignment(assignmentId, memberId);
      await ContactCenterV3Model.loadConversations();
    } catch (e) {
      warn("ContactCenterV3Model.rejectAssignment failed", e);
    }
  }

  static async completeAssignment(assignmentId: number, returnToBot?: boolean): Promise<void> {
    const memberId = Member.id;
    if (!memberId) return;

    try {
      await API.ContactCenterV3.completeAssignment(assignmentId, memberId, returnToBot);
      // Immediate presence recompute → online if this was the last assignment.
      BotPresenceManager.heartbeat();
    } catch (e) {
      warn("ContactCenterV3Model.completeAssignment failed", e);
      // The server rejects a complete on an assignment it already closed (the
      // call ended under us) — say so instead of a silent no-op (ENG-3447).
      ContactCenterV3Model.showCCNotice("error", "Couldn't leave this conversation — the takeover may already be over. Refreshing.");
    }
    // Ending a takeover ends the THREAD, never the user (ENG-3036) — the
    // user stays in Open and stays selected; refresh the detail in place.
    // Also on failure, so a stale composer clears (ENG-3447).
    await ContactCenterV3Model.loadConversations();
    await ContactCenterV3Model.loadConversationDetail(undefined, undefined, undefined, { silent: true });
  }

  /**
   * Hang up a voice takeover (ENG-3448) — the voice row's action. The bot ends
   * this member's own agent leg: bridged, the call ends only if no other agent
   * is still on it (ENG-3821); still ringing, the caller goes back to the bot.
   * This tab's softphone drops with the leg; the assignment completes through
   * the call's own end path.
   */
  static async hangupVoiceCall(botConversationId: number): Promise<void> {
    const memberId = Member.id;
    if (!memberId) return;
    try {
      await API.ContactCenterV3.hangupVoiceCall(botConversationId, memberId);
      if (ContactCenterV3Model.state.activeCallSid === String(botConversationId)) {
        const { CCVoiceDevice } = await import("./CCVoiceDevice");
        CCVoiceDevice.hangup();
      }
      BotPresenceManager.heartbeat();
    } catch (e) {
      warn("ContactCenterV3Model.hangupVoiceCall failed", e);
      ContactCenterV3Model.showCCNotice("error", "Couldn't hang up this call. Refreshing.");
    }
    await ContactCenterV3Model.loadConversations();
    await ContactCenterV3Model.loadConversationDetail(undefined, undefined, undefined, { silent: true });
  }

  static async transferAssignment(assignmentId: number, toMemberId: number, handoffNote?: string): Promise<void> {
    const memberId = Member.id;
    if (!memberId) return;

    try {
      await API.ContactCenterV3.transferAssignment(assignmentId, memberId, toMemberId, handoffNote);
      // Immediate presence recompute → online if this was the last assignment.
      BotPresenceManager.heartbeat();
      await ContactCenterV3Model.loadConversations();
    } catch (e) {
      warn("ContactCenterV3Model.transferAssignment failed", e);
    }
  }

  static async proactiveTakeover(botConversationId: number, channel: string): Promise<void> {
    const memberId = Member.id;
    if (!memberId) return;

    try {
      // The takeover may own a FRESH conversation (finalized thread, ENG-3036)
      // — coaching/facility must follow the returned target, not the clicked
      // id, or the panel keeps showing the previous conversation's nudges and
      // drops the new one's socket events (ENG-3051, Chuck 2026-08-20).
      const result = await API.ContactCenterV3.proactiveTakeover(botConversationId, memberId, channel);
      const targetConversationId = result.botConversationId ?? botConversationId;
      // Immediate presence recompute → busy (agent now owns this conversation).
      BotPresenceManager.heartbeat();
      ContactCenterV3Model.setState({ rightPanelTab: "assist" });
      await ContactCenterV3Model.loadConversations();
      await ContactCenterV3Model.loadConversationDetail(undefined, undefined, undefined, { silent: true });

      void ContactCenterV3Model.loadCoachingState(targetConversationId);
      void ContactCenterV3Model.refreshFacility(targetConversationId);
    } catch (e) {
      warn("ContactCenterV3Model.proactiveTakeover failed", e);
      // Surface the server's reason rather than leaving the click dead — e.g.
      // a closed conversation refuses the takeover. Matches `pullTakeover`
      // and `closeConversation`; this was the only takeover path that
      // swallowed the failure into the console (ENG-3020).
      const message = e instanceof Error && e.message && !e.message.startsWith("HTTP error") ? e.message : "Failed to take over the conversation";
      ContactCenterV3Model.showError(message);
    }
  }

  /** "Take over from X" — pull an actively-owned conversation (ENG-2946). */
  static async pullTakeover(botConversationId: number, channel: string): Promise<void> {
    const memberId = Member.id;
    if (!memberId) return;

    try {
      await API.ContactCenterV3.pullTakeover(botConversationId, memberId, channel);
      BotPresenceManager.heartbeat();
      ContactCenterV3Model.setState({ rightPanelTab: "assist" });
      await ContactCenterV3Model.loadConversations();
      await ContactCenterV3Model.loadConversationDetail(undefined, undefined, undefined, { silent: true });
      void ContactCenterV3Model.loadCoachingState(botConversationId);
      void ContactCenterV3Model.refreshFacility(botConversationId);
    } catch (e) {
      warn("ContactCenterV3Model.pullTakeover failed", e);
      const message = e instanceof Error && e.message && !e.message.startsWith("HTTP error") ? e.message : "Failed to take over the conversation";
      ContactCenterV3Model.showError(message);
    }
  }

  // ─── Tab State ─────────────────────────────────────────────────────────

  static setActiveTab(tab: CCActiveTab): void {
    ContactCenterV3Model.setState({ activeTab: tab });
  }

  static setRightPanelTab(tab: CCRightPanelTab): void {
    ContactCenterV3Model.setState({ rightPanelTab: tab });
  }

  static async setMessageVisibility(level: DebugLevel): Promise<void> {
    ContactCenterV3Model.setState({ messageVisibility: level });
    // The selection persists across sessions (Chuck, 2026-07-27).
    try {
      localStorage.setItem(DEBUG_LEVEL_STORAGE_KEY, level);
    } catch {
      /* storage unavailable — session-only */
    }
    // Re-fetch conversation detail with new visibility level
    await ContactCenterV3Model.loadConversationDetail(undefined, undefined, undefined, { silent: true });
  }

  /**
   * Initial debug level (ENG-2946): the persisted selection wins; otherwise
   * SwivlAdmin accounts start at admin, everyone else at agent — clamped to
   * the account-kind ceiling either way.
   */
  static initMessageVisibility(): void {
    const ceiling: DebugLevel = Member.isSwivlStaff ? "admin" : "agent";
    let level: DebugLevel = Member.isSwivlAdmin ? "admin" : "agent";
    try {
      const stored = localStorage.getItem(DEBUG_LEVEL_STORAGE_KEY) as DebugLevel | null;
      if (stored === "none" || stored === "agent" || stored === "admin") level = stored;
    } catch {
      /* storage unavailable — kind default */
    }
    if (!debugLevelAtLeast(ceiling, level)) level = ceiling;
    ContactCenterV3Model.setState({ messageVisibility: level });
  }

  static setSelectedChannel(channel: string | null): void {
    ContactCenterV3Model.setState({ selectedChannel: channel });
    // Switching onto a channel tab = viewing that conversation — stamp its
    // read watermark now (detail-open only stamps the initially visible
    // channel; see loadConversationDetail).
    if (channel && typeof document !== "undefined" && !document.hidden) {
      const ch = ContactCenterV3Model.state.conversationDetail?.channels.find((c) => c.channel === channel);
      if (ch) {
        BotPresenceManager.markRead(ch.botConversationId);
        ContactCenterV3Model.clearListUnread(ch.botConversationId);
      }
    }
  }

  // ─── Coaching (ENG-2957) ───────────────────────────────────────────────

  /** Reload-state fetch: latest snapshot + full nudge list for a conversation. */
  static async loadCoachingState(botConversationId: number): Promise<void> {
    const orgId = Member.orgId;
    if (!orgId) return;
    // Switching conversations (e.g. a SECOND takeover = a fresh conversation,
    // ENG-3036) clears the previous conversation's coaching state IMMEDIATELY —
    // stale cards used to stay on screen until the new fetch resolved, so the
    // panel showed another takeover's nudges (Chuck, 2026-08-20).
    const isSwitch = ContactCenterV3Model.state.coachingConversationId !== botConversationId;
    ContactCenterV3Model.setState({
      isLoadingCoaching: true,
      coachingConversationId: botConversationId,
      ...(isSwitch
        ? {
            coachingSnapshot: null,
            coachingScoreVelocity: null,
            coachingNudges: [],
            coachingFocus: null,
            coachingTopics: [],
            coachingScript: null,
            unseenNudgeIds: []
          }
        : {})
    });
    try {
      const state = await API.ContactCenterV3.getCoachingState(orgId, botConversationId);
      // Guard: the agent may have switched conversations while the fetch ran.
      if (ContactCenterV3Model.state.coachingConversationId !== botConversationId) return;
      ContactCenterV3Model.setState({
        coachingSnapshot: state.latestSnapshot,
        coachingScoreVelocity: state.scoreVelocity ?? null,
        coachingNudges: state.nudges,
        coachingFocus: state.focus,
        coachingTopics: state.topics ?? [],
        coachingScript: state.script ?? null,
        isLoadingCoaching: false,
        unseenNudgeIds: []
      });
    } catch (e) {
      warn("ContactCenterV3Model.loadCoachingState failed", e);
      ContactCenterV3Model.setState({ isLoadingCoaching: false });
    }
  }

  /** Socket ingest: coaching:score — replace the snapshot (payloads carry full persisted rows). */
  static ingestCoachingScore(payload: {
    botConversationId: number;
    snapshot: CoachingScoreSnapshotInterface;
    topics?: string[];
    /** Trend of the score as of this tick (ENG-3068) — see coachingScoreVelocity. */
    scoreVelocity?: number;
  }): void {
    if (ContactCenterV3Model.state.coachingConversationId !== payload.botConversationId) return;
    ContactCenterV3Model.setState({
      coachingSnapshot: payload.snapshot,
      ...(payload.scoreVelocity !== undefined ? { coachingScoreVelocity: payload.scoreVelocity } : {}),
      // Header pills update live off the score event (Chuck, 2026-08-18).
      ...(payload.topics ? { coachingTopics: payload.topics } : {})
    });
  }

  /** Socket ingest: coaching:nudge — upsert by id (fires AND lifecycle updates ride the same event). */
  static ingestCoachingNudge(payload: { botConversationId: number; nudge: CoachingNudgeInterface }): void {
    if (ContactCenterV3Model.state.coachingConversationId !== payload.botConversationId) return;
    const existing = ContactCenterV3Model.state.coachingNudges;
    const known = existing.some((n) => n.id === payload.nudge.id);
    const next = known ? existing.map((n) => (n.id === payload.nudge.id ? payload.nudge : n)) : [...existing, payload.nudge];
    const unseen = known ? ContactCenterV3Model.state.unseenNudgeIds : [...ContactCenterV3Model.state.unseenNudgeIds, payload.nudge.id];
    ContactCenterV3Model.setState({ coachingNudges: next, unseenNudgeIds: unseen });
  }

  /**
   * Socket ingest: coaching:focus — the BOT set the conversation's focus
   * (load_location/load_unit, widget tap; ENG-3051). Refetch ONLY the focus so
   * the Selected box updates live without clearing the unseen-nudge signal.
   */
  static async ingestCoachingFocus(payload: { botConversationId: number }): Promise<void> {
    if (ContactCenterV3Model.state.coachingConversationId !== payload.botConversationId) return;
    const orgId = Member.orgId;
    if (!orgId) return;
    try {
      const state = await API.ContactCenterV3.getCoachingState(orgId, payload.botConversationId);
      if (ContactCenterV3Model.state.coachingConversationId !== payload.botConversationId) return;
      ContactCenterV3Model.setState({ coachingFocus: state.focus });
    } catch (e) {
      warn("ContactCenterV3Model.ingestCoachingFocus failed", e);
    }
  }

  /** Socket ingest: coaching:script — the engine judged script steps done (ENG-3402); the payload carries the whole view. */
  static ingestCoachingScript(payload: { botConversationId: number; script: CallScriptAgentView | null }): void {
    if (ContactCenterV3Model.state.coachingConversationId !== payload.botConversationId) return;
    ContactCenterV3Model.setState({ coachingScript: payload.script ?? null });
  }

  /** Socket ingest: coaching:summary — running-summary refresh onto the current snapshot. */
  static ingestCoachingSummary(payload: { botConversationId: number; runningSummary: string }): void {
    if (ContactCenterV3Model.state.coachingConversationId !== payload.botConversationId) return;
    const snapshot = ContactCenterV3Model.state.coachingSnapshot;
    if (!snapshot) return;
    ContactCenterV3Model.setState({ coachingSnapshot: { ...snapshot, runningSummary: payload.runningSummary } });
  }

  /**
   * Selected box (Chuck, 2026-08-18): set or clear the conversation's focused
   * location/unit. `null` clears (server clears the unit with the location).
   * Refreshes coaching state so the box + toolbar gating update immediately.
   */
  static async setCoachingFocus(body: { locationId?: number | null; unitId?: number | null }): Promise<void> {
    const orgId = Member.orgId;
    const convId = ContactCenterV3Model.state.coachingConversationId;
    if (!orgId || !convId) return;
    try {
      await API.ContactCenterV3.coachingSetFocus(orgId, convId, body);
      void ContactCenterV3Model.loadCoachingState(convId);
    } catch (e) {
      warn("ContactCenterV3Model.setCoachingFocus failed", e);
      ContactCenterV3Model.showError("Couldn't update the selection — try again");
    }
  }

  /** Topic pill removal (2026-08-18): optimistic remove; the server suppresses re-adds. */
  static async removeCoachingTopic(topic: string): Promise<void> {
    const orgId = Member.orgId;
    const convId = ContactCenterV3Model.state.coachingConversationId;
    if (!orgId || !convId) return;
    const prev = ContactCenterV3Model.state.coachingTopics;
    ContactCenterV3Model.setState({ coachingTopics: prev.filter((t) => t !== topic) });
    try {
      await API.ContactCenterV3.coachingRemoveTopic(orgId, convId, topic);
    } catch (e) {
      warn("ContactCenterV3Model.removeCoachingTopic failed", e);
      ContactCenterV3Model.setState({ coachingTopics: prev });
      ContactCenterV3Model.showError("Couldn't remove the tag — try again");
    }
  }

  /** Selected box (2026-08-18): send the selected location/unit to the customer as a structured card. */
  static async sendFocusCard(kind: "location" | "unit"): Promise<void> {
    const orgId = Member.orgId;
    const memberId = Member.id;
    const convId = ContactCenterV3Model.state.coachingConversationId;
    if (!orgId || !memberId || !convId) return;
    try {
      await API.ContactCenterV3.coachingSendFocusCard(orgId, convId, { memberId, kind, messageId: uuid() });
    } catch (e) {
      warn("ContactCenterV3Model.sendFocusCard failed", e);
      const message = e instanceof Error && e.message && !e.message.startsWith("HTTP error") ? e.message : "Couldn't send — try again";
      ContactCenterV3Model.showError(message);
    }
  }

  /** The nudge list reached the bottom / the "New live nudge" pill was clicked. */
  static markNudgesSeen(): void {
    if (ContactCenterV3Model.state.unseenNudgeIds.length > 0) ContactCenterV3Model.setState({ unseenNudgeIds: [] });
  }

  /**
   * The agent clicked INTO one new card, so that card stops glowing while any
   * others keep theirs (ENG-3186). Deliberately narrower than `markNudgesSeen`:
   * jumping to a card is not the same as engaging with it — the glow is what
   * says "you haven't looked at this one yet".
   */
  static markNudgeEngaged(nudgeId: number): void {
    const { unseenNudgeIds } = ContactCenterV3Model.state;
    if (!unseenNudgeIds.includes(nudgeId)) return;
    ContactCenterV3Model.setState({ unseenNudgeIds: unseenNudgeIds.filter((id) => id !== nudgeId) });
  }

  /** Pin/unpin/dismiss a nudge card. Await result + socket echo keep every watcher in sync. */
  static async nudgeLifecycle(nudgeId: number, action: "pin" | "unpin" | "dismiss"): Promise<void> {
    const memberId = Member.id;
    if (!memberId) return;
    try {
      const nudge = await API.ContactCenterV3.coachingNudgeLifecycle(nudgeId, memberId, action);
      ContactCenterV3Model.ingestCoachingNudge({ botConversationId: nudge.botConversationId, nudge });
    } catch (e) {
      warn("ContactCenterV3Model.nudgeLifecycle failed", e);
      ContactCenterV3Model.showError("Couldn't update the nudge");
    }
  }

  /** Execute a nudge action (send/text/units) as the agent. The card finishes on success. */
  /**
   * Agent toolbar (§13.7): pull up a Locations / Units / Policies card on
   * demand. The bot builds + persists an agent-selected nudge and broadcasts
   * it — the card arrives through the normal coaching:nudge socket ingest.
   */
  static async createAgentNudge(type: string, param?: string): Promise<boolean> {
    const { coachingConversationId } = this.useState.getState();
    if (!Member.orgId || !Member.id || !coachingConversationId) return false;
    try {
      const result = await API.ContactCenterV3.coachingAgentNudge(Member.orgId, coachingConversationId, {
        type,
        param,
        memberId: Member.id
      });
      return result.nudgeId !== null;
    } catch (e) {
      warn("createAgentNudge failed", e);
      return false;
    }
  }

  /**
   * Policy nudge on-expand draft (§13.6): one default-model call server-side
   * writes the agent's message from the location's policy + the conversation.
   * Called when the card opens; the caller shows a loading state meanwhile.
   */
  static async generatePolicyDraft(nudgeId: number, policyKey: string, agentSelected?: boolean): Promise<string> {
    const { coachingConversationId } = this.useState.getState();
    if (!Member.orgId || !coachingConversationId) return "";
    try {
      const result = await API.ContactCenterV3.coachingPolicyDraft(Member.orgId, coachingConversationId, nudgeId, policyKey, agentSelected);
      return result.message ?? "";
    } catch (e) {
      warn("policy draft failed", e);
      return "";
    }
  }

  static async executeNudge(
    nudgeId: number,
    actionKind: string,
    opts?: {
      text?: string;
      unitIds?: number[];
      locationIds?: number[];
      locationId?: number;
      unitId?: number;
      sendCard?: boolean;
      skipSelect?: boolean;
    }
  ): Promise<boolean> {
    const memberId = Member.id;
    if (!memberId) return false;
    try {
      const nudge = await API.ContactCenterV3.coachingNudgeExecute(nudgeId, {
        memberId,
        actionKind,
        text: opts?.text,
        unitIds: opts?.unitIds,
        locationIds: opts?.locationIds,
        locationId: opts?.locationId,
        unitId: opts?.unitId,
        sendCard: opts?.sendCard,
        skipSelect: opts?.skipSelect,
        messageId: uuid()
      });
      ContactCenterV3Model.ingestCoachingNudge({ botConversationId: nudge.botConversationId, nudge });
      if (actionKind === "set_location" || actionKind === "set_unit" || actionKind === "send_units" || actionKind === "send_locations") {
        // Focus changed — refresh the §13.5 context (and the nudge list) so the
        // header card and toolbar disabled-states update immediately.
        // send_units included: a single-unit send auto-focuses that unit
        // server-side (Chuck, 2026-08-18).
        const convId = ContactCenterV3Model.state.coachingConversationId;
        if (convId) void ContactCenterV3Model.loadCoachingState(convId);
      }
      return true;
    } catch (e) {
      warn("ContactCenterV3Model.executeNudge failed", e);
      const message = e instanceof Error && e.message && !e.message.startsWith("HTTP error") ? e.message : "Couldn't send — try again";
      ContactCenterV3Model.showError(message);
      return false;
    }
  }

  // ─── Right column data (ENG-2957) ──────────────────────────────────────

  /** Snowy-native user detail: header card, facility/lease card, History tab rows. */
  static async loadUserDetail(userId: number): Promise<void> {
    const orgId = Member.orgId;
    if (!orgId) return;
    ContactCenterV3Model.setState({ isLoadingUserDetail: true });
    try {
      const detail = await API.ContactCenterV3.getUserDetail(orgId, userId);
      // Guard: the agent may have switched users while the fetch ran.
      if (ContactCenterV3Model.state.selectedUserId !== userId) return;
      ContactCenterV3Model.setState({ ccUserDetail: detail, isLoadingUserDetail: false });
    } catch (e) {
      warn("ContactCenterV3Model.loadUserDetail failed", e);
      ContactCenterV3Model.setState({ isLoadingUserDetail: false });
    }
  }

  /**
   * Header-card inline edit (ENG-3186 — restores the v2 "Edit" affordance):
   * save the customer's name/phone/email, then re-read the panel detail so
   * every card reflects the change. Returns whether the save landed, so the
   * form can stay open (with the agent's input intact) on failure.
   */
  static async updateCustomerDetails(fields: { name?: string; phoneNumber?: string; email?: string }): Promise<boolean> {
    const orgId = Member.orgId;
    const userId = ContactCenterV3Model.state.selectedUserId;
    if (!orgId || !userId) return false;
    try {
      await API.Users.updateUser(orgId, userId, fields);
      // No success notice: the re-read below repaints the card with the new
      // values, which IS the confirmation. The `ccNotices` stack's errors are
      // sticky by design (missed offers, presence changes); a per-save card
      // there would still be noise.
      await ContactCenterV3Model.loadUserDetail(userId);
      return true;
    } catch (e) {
      warn("ContactCenterV3Model.updateCustomerDetails failed", e);
      ContactCenterV3Model.showError("Couldn't save the customer details — try again");
      return false;
    }
  }

  /**
   * Kebab menu (ENG-3186): renew the customer's transcript-link access so the
   * copied URL stays valid for another 3 days. Best-effort — the link is
   * already on the clipboard by the time this runs.
   */
  static async renewTranscriptAccess(): Promise<void> {
    const orgId = Member.orgId;
    const userId = ContactCenterV3Model.state.selectedUserId;
    if (!orgId || !userId) return;
    try {
      await API.Users.renewTranscriptAccess(orgId, userId);
    } catch (e) {
      warn("ContactCenterV3Model.renewTranscriptAccess failed", e);
    }
  }

  /**
   * Kebab menu (ENG-3191): read whether this customer is blocked.
   *
   * Blocking is a USER VAR (`User:IsBlocked`), not a column — the server reads
   * it in `Escalations.Service` and answers a blocked customer's escalation
   * with "no agents available" instead of queueing it.
   */
  static async fetchCustomerBlocked(userId: number): Promise<boolean> {
    const orgId = Member.orgId;
    if (!orgId) return false;
    try {
      const vars = await API.UserVars.getUserVars(orgId, userId, [UserVarKey.IsBlocked]);
      return isUserBlockedValue(vars[UserVarKey.IsBlocked]);
    } catch (e) {
      warn("ContactCenterV3Model.fetchCustomerBlocked failed", e);
      return false;
    }
  }

  /**
   * Kebab menu (ENG-3191): block or unblock this customer.
   *
   * Unblocking writes an EMPTY STRING rather than "false". `VarsService.getVar`
   * returns a string and the server's check is a bare `if (isBlocked)`, so
   * "false" is a non-empty string and would leave the customer blocked.
   */
  static async setCustomerBlocked(userId: number, blocked: boolean): Promise<boolean> {
    const orgId = Member.orgId;
    if (!orgId) return false;
    try {
      await API.UserVars.saveUserVar(orgId, userId, UserVarKey.IsBlocked, blocked ? "true" : "");
      return true;
    } catch (e) {
      warn("ContactCenterV3Model.setCustomerBlocked failed", e);
      ContactCenterV3Model.showError(blocked ? "Couldn't block this customer — try again" : "Couldn't unblock this customer — try again");
      return false;
    }
  }

  /** Facility JIT refresh (plan §9.5): re-sync the user's FMS data, then re-read the detail. */
  static async refreshFacility(botConversationId: number): Promise<void> {
    const orgId = Member.orgId;
    const userId = ContactCenterV3Model.state.selectedUserId;
    if (!orgId || !userId || ContactCenterV3Model.state.isRefreshingFacility) return;
    ContactCenterV3Model.setState({ isRefreshingFacility: true });
    try {
      const result = await API.ContactCenterV3.refreshFacilityData(orgId, botConversationId);
      // Nothing queued means nothing is being refreshed. Say so instead of
      // stamping a fresh timestamp over unchanged data — a refresh that
      // silently did nothing used to look identical to one that worked.
      if (result.total > 0 && result.queued === 0) {
        ContactCenterV3Model.setState({ isRefreshingFacility: false });
        ContactCenterV3Model.showError("Couldn't reach the sync service — facility data wasn't refreshed.");
        return;
      }
      // The re-sync is a queued bull job, not synchronous — give it a grace
      // period before re-reading, or we'd just read back the stale rows.
      await new Promise((resolve) => setTimeout(resolve, 4000));
      await ContactCenterV3Model.loadUserDetail(userId);
      ContactCenterV3Model.setState({ isRefreshingFacility: false, facilityDataAsOf: result.refreshedAt });
    } catch (e) {
      warn("ContactCenterV3Model.refreshFacility failed", e);
      ContactCenterV3Model.setState({ isRefreshingFacility: false });
      ContactCenterV3Model.showError("Couldn't refresh facility data — try again.");
    }
  }

  // ─── Voice ─────────────────────────────────────────────────────────────

  /**
   * Per-page-load session suffix for the Twilio Client identity (ENG-3017).
   * Every CC tab used to register the SAME identity, so incoming agent legs
   * rang all of them and the fastest auto-accept stole the call. With this,
   * each tab is a distinct endpoint and calls land where they were dialed.
   */
  private static readonly voiceSessionId: string = Math.random().toString(36).slice(2, 10);

  /** The token fetch in flight, so two Devices expiring together (or two init callers) share one request. */
  private static voiceTokensInFlight: Promise<CCVoiceToken[]> | null = null;

  /**
   * Every softphone token this tab needs: one per Twilio account the org's
   * numbers live on (ENG-3656). All share this tab's identity. Empty when the
   * agent has no org/member or the server could not mint any.
   */
  static async ensureVoiceTokens(): Promise<CCVoiceToken[]> {
    const cached = ContactCenterV3Model.state.twilioTokens;
    if (cached && cached.length > 0) return cached;
    if (ContactCenterV3Model.voiceTokensInFlight) return ContactCenterV3Model.voiceTokensInFlight;

    const org = Member.orgId;
    const member = Member.id;
    if (!org || !member) return [];

    ContactCenterV3Model.voiceTokensInFlight = (async (): Promise<CCVoiceToken[]> => {
      try {
        const tokens = await API.ContactCenterV3.generateVoiceTokens(org, member, ContactCenterV3Model.voiceSessionId);
        ContactCenterV3Model.setState({
          twilioTokens: tokens,
          twilioToken: tokens[0]?.token ?? null,
          twilioIdentity: tokens[0]?.identity ?? null
        });
        return tokens;
      } catch (e) {
        warn("ContactCenterV3Model.ensureVoiceTokens failed", e);
        return [];
      } finally {
        ContactCenterV3Model.voiceTokensInFlight = null;
      }
    })();
    return ContactCenterV3Model.voiceTokensInFlight;
  }

  /** The first softphone token: what the voice action calls send so the bot can read this tab's identity. */
  static async ensureVoiceToken(): Promise<string | null> {
    if (ContactCenterV3Model.state.twilioToken) return ContactCenterV3Model.state.twilioToken;
    const tokens = await ContactCenterV3Model.ensureVoiceTokens();
    return tokens[0]?.token ?? null;
  }

  static async listenIn(botConversationId: number): Promise<void> {
    const orgId = Member.orgId;
    if (!orgId) return;

    const token = await ContactCenterV3Model.ensureVoiceToken();
    if (!token) return;

    try {
      const { CCVoiceDevice } = await import("./CCVoiceDevice");
      CCVoiceDevice.setPendingAction("listen", String(botConversationId));
      await API.ContactCenterV3.listenIn(botConversationId, orgId, token);
      ContactCenterV3Model.setState({ activeCallSid: String(botConversationId), voiceState: "listening", isMuted: true });
    } catch (e) {
      warn("ContactCenterV3Model.listenIn failed", e);
      ContactCenterV3Model.voiceActionFailed("listen in", e);
    }
  }

  /**
   * A voice action the bot refused — most often because this conversation's
   * call has already ended (ENG-3656). Say so and reload the detail so the live
   * conversation, if there is one, replaces what is on screen.
   */
  private static voiceActionFailed(what: string, error?: unknown): void {
    // The pending action was armed before the API call; left in place it would
    // claim the next unrelated incoming leg as this listen / barge.
    void import("./CCVoiceDevice").then(({ CCVoiceDevice }) => CCVoiceDevice.clearPendingAction());
    // The bot's own reason when it gave one (a 4xx with a message: someone already listening,
    // the call already over, ENG-3807); the guess only when it did not.
    const status = (error as { status?: number } | undefined)?.status;
    const reason = error instanceof Error && typeof status === "number" && status >= 400 && status < 500 ? error.message : undefined;
    ContactCenterV3Model.showCCNotice(
      "error",
      reason ? `Couldn't ${what} — ${reason} Refreshing.` : `Couldn't ${what} — this call may have ended. Refreshing.`
    );
    const { selectedUserId, selectedChannel } = ContactCenterV3Model.state;
    if (selectedUserId != null)
      void ContactCenterV3Model.loadConversationDetail(Member.orgId, selectedUserId, selectedChannel ?? undefined, { silent: true });
  }

  /**
   * Barging into a live call is ALWAYS a takeover (Chuck, 2026-08-20) —
   * Listen In is the only non-takeover voice action. Join Call / Barge In
   * used to bridge raw into the conference with no assignment and no
   * escalation, so coaching stayed in bot phase (no score ticks, no nudges,
   * agent speech ignored) and the CC had no record the agent was on the call.
   */
  static async voiceBargeTakeover(botConversationId: number): Promise<void> {
    const detail = ContactCenterV3Model.state.conversationDetail;
    const channelData = detail?.channels.find((c) => c.botConversationId === botConversationId);
    const assignment = detail?.activeAssignment;
    const isMine = !!assignment && assignment.status === "accepted" && assignment.agentMemberId === Member.id;
    if (isMine) return; // accept-path bridge — the takeover already exists
    if (channelData?.escalationStatus === "active") {
      // Someone else is on it — barging is a pull.
      await ContactCenterV3Model.pullTakeover(botConversationId, "voice");
      return;
    }
    await ContactCenterV3Model.proactiveTakeover(botConversationId, "voice");
  }

  static async bargeIn(botConversationId: number): Promise<void> {
    const orgId = Member.orgId;
    if (!orgId) return;

    const token = await ContactCenterV3Model.ensureVoiceToken();
    if (!token) return;

    try {
      // Becoming audible = taking over (Chuck, 2026-08-20).
      await ContactCenterV3Model.voiceBargeTakeover(botConversationId);
      const { CCVoiceDevice } = await import("./CCVoiceDevice");
      CCVoiceDevice.setPendingAction("barge", String(botConversationId));
      await API.ContactCenterV3.bargeIn(botConversationId, orgId, token);
      ContactCenterV3Model.setState({ voiceState: "active", isMuted: false });
    } catch (e) {
      warn("ContactCenterV3Model.bargeIn failed", e);
      ContactCenterV3Model.voiceActionFailed("join the call", e);
    }
  }

  static async bridgeToAgent(botConversationId: number): Promise<void> {
    const orgId = Member.orgId;
    if (!orgId) return;

    try {
      // The bot dials THIS tab's identity — the Device must be registered
      // BEFORE the dial or Twilio reports no-answer within milliseconds
      // (ENG-3448 test, 2026-09-29: Accept from the Hours page after a fresh
      // load minted a token for an identity nothing was listening on).
      // init() is idempotent; the outbound path has done this since August.
      const { CCVoiceDevice } = await import("./CCVoiceDevice");
      await CCVoiceDevice.init();
      const token = await ContactCenterV3Model.ensureVoiceToken();
      if (!token) {
        ContactCenterV3Model.showCCNotice("error", "Couldn't set up your softphone for this call. Reload and accept again.");
        return;
      }
      CCVoiceDevice.setPendingAction("bridge", String(botConversationId));
      // "Ringing…" until the softphone actually connects — the Device flips
      // this to active on accept. Claiming active here painted call controls
      // over a call that never reached the browser.
      ContactCenterV3Model.setState({ activeCallSid: String(botConversationId), voiceState: "dialing", isMuted: false });
      await API.ContactCenterV3.bridgeToAgent(botConversationId, orgId, token);

      // Watchdog: the bot gives the leg 30 s; if nothing reached this browser by
      // then, drop the bar instead of leaving it wedged on "Ringing…".
      const watched = String(botConversationId);
      setTimeout(() => {
        const s = ContactCenterV3Model.state;
        if (s.voiceState === "dialing" && s.activeCallSid === watched) {
          warn("ContactCenterV3Model.bridgeToAgent: the agent leg never reached this browser — resetting");
          ContactCenterV3Model.endVoiceSession();
        }
      }, 35_000);
    } catch (e) {
      warn("ContactCenterV3Model.bridgeToAgent failed", e);
      ContactCenterV3Model.endVoiceSession();
      ContactCenterV3Model.showCCNotice("error", "Couldn't join the call. The caller is still on hold with the bot.");
    }
  }

  /**
   * Returns the customer's userId (existing or just-created) so callers can land on their
   * conversation, or null on failure. The Outbound Gate (ENG-3373) may answer instead of
   * dialing: `simulated` → nothing rang, the conversation says why, the agent is told;
   * `blocked` → the person opted out of calls — the agent is asked once and, on "Call anyway",
   * the request is repeated with `consentOverride` (decision 13).
   */
  static async initiateOutboundCall(toNumber: string, userId?: number, fromNumber?: string, consentOverride = false): Promise<number | null> {
    const orgId = Member.orgId;
    const memberId = Member.id;
    if (!orgId || !memberId) return null;

    try {
      // The bot dials this agent's client into the conference immediately —
      // the Device must be registered BEFORE dialing or the agent leg never
      // reaches the browser. init() is idempotent and mints the voice token.
      const { CCVoiceDevice } = await import("./CCVoiceDevice");
      await CCVoiceDevice.init();

      // Pass this session's token so the agent leg dials THIS tab's identity
      // — not every tab this member has open.
      const token = await ContactCenterV3Model.ensureVoiceToken();

      ContactCenterV3Model.setState({ voiceState: "dialing" });
      const result = await API.ContactCenterV3.initiateOutboundCall(
        orgId,
        memberId,
        toNumber,
        fromNumber,
        token ?? undefined,
        userId,
        consentOverride
      );
      if (result.outcome === "blocked") {
        ContactCenterV3Model.setState({ voiceState: "idle" });
        const callAnyway = window.confirm("This person has opted out of calls. Call anyway?");
        if (!callAnyway) return result.userId ?? null;
        return ContactCenterV3Model.initiateOutboundCall(toNumber, userId, fromNumber, true);
      }
      if (result.outcome === "simulated") {
        ContactCenterV3Model.setState({ voiceState: "idle" });
        ContactCenterV3Model.showError(`Call simulated — nothing was dialed (${result.reason ?? "not allowed"}). The conversation records it.`);
        return result.userId ?? null;
      }
      // Stay in "dialing" — CCVoiceDevice flips to "active" when the agent
      // leg actually connects. Claiming "active" here painted a live call
      // that didn't exist whenever the leg failed to arrive.
      ContactCenterV3Model.setState({ activeCallSid: result.callSid });

      // Watchdog: if the agent leg never arrives, reset instead of leaving
      // the page wedged in a state that hides every Call button (the bug
      // that ate 2026-08-12).
      const watchedCallSid = result.callSid;
      setTimeout(() => {
        const s = ContactCenterV3Model.state;
        if (s.voiceState === "dialing" && s.activeCallSid === watchedCallSid) {
          warn("ContactCenterV3Model.initiateOutboundCall: agent leg never connected — resetting");
          ContactCenterV3Model.endVoiceSession();
          ContactCenterV3Model.showError("Call setup timed out — the call audio never reached your browser. Try again.");
        }
      }, 30_000);
      return result.userId ?? null;
    } catch (e) {
      warn("ContactCenterV3Model.initiateOutboundCall failed", e);
      ContactCenterV3Model.setState({ voiceState: "idle" });
      // Surface WHY — the swallowed version of this error hid an allowlist
      // block behind "nothing happens" for a full day (ENG-3017).
      const message = e instanceof Error ? e.message : "Call failed";
      ContactCenterV3Model.showError(`Call failed: ${message}`);
      return null;
    }
  }

  /**
   * A reply the carrier did not deliver tells the agent so (ENG-3281, decision 4a): the row
   * shows the mark, and the notice says why, in the words New Conversation uses.
   */
  static noticeForWithheldText(deliveryStatus: string | undefined, reason: string | undefined): void {
    if (deliveryStatus === "simulated") {
      ContactCenterV3Model.showCCNotice("error", `Text simulated, nothing was sent. ${simulatedTextReason(reason)}`);
    } else if (deliveryStatus === "blocked") {
      ContactCenterV3Model.showCCNotice("error", "This customer has opted out of texts. Nothing was sent.");
    } else if (deliveryStatus === "failed") {
      ContactCenterV3Model.showCCNotice(
        "error",
        reason === "bot_unreachable" ? "The text couldn't be sent: the bot server didn't answer. Try again." : "The text couldn't be sent. Try again."
      );
    }
  }

  /**
   * New Conversation's Text (ENG-3592): send an agent's 1:1 text and show the result as a
   * CC notice. On `sent` the server has already taken the new thread over for this agent.
   * Returns the result (null when the request failed) so the modal can land on the SMS tab.
   */
  static async startOutboundText(params: {
    userId?: number;
    toNumber?: string;
    fromNumber?: string;
    content: string;
    customerName?: string;
  }): Promise<AgentOutboundTextResult | null> {
    const orgId = Member.orgId;
    if (!orgId) return null;

    const { customerName, ...body } = params;
    try {
      const result = await API.ContactCenterV3.startOutboundText(orgId, body);
      if (result.outcome === "sent") {
        if (result.takeoverStarted) {
          ContactCenterV3Model.showCCNotice("success", `Text sent to ${customerName || "the customer"}. Replies come to you.`);
          // Immediate presence recompute → busy (the agent now owns the thread).
          BotPresenceManager.heartbeat();
          ContactCenterV3Model.setState({ rightPanelTab: "assist" });
        } else {
          ContactCenterV3Model.showCCNotice("error", "Text sent, but the takeover didn't start. Use Takeover on the SMS tab.");
        }
      } else if (result.outcome === "simulated") {
        ContactCenterV3Model.showCCNotice("error", `Text simulated, nothing was sent. ${simulatedTextReason(result.reason)}`);
      } else if (result.outcome === "blocked") {
        ContactCenterV3Model.showCCNotice("error", "This customer has opted out of texts. Nothing was sent.");
      } else {
        ContactCenterV3Model.showCCNotice("error", "The text couldn't be sent. Try again.");
      }
      void ContactCenterV3Model.loadConversations();
      return result;
    } catch (e) {
      warn("ContactCenterV3Model.startOutboundText failed", e);
      // The server's refusals are written for the agent ("…is with Riley Chen right now").
      const message = e instanceof Error && e.message && !e.message.startsWith("HTTP error") ? e.message : "The text couldn't be sent. Try again.";
      ContactCenterV3Model.showCCNotice("error", message);
      return null;
    }
  }

  /**
   * After a text to the customer already on screen (ENG-3592): refresh their detail, show the
   * SMS tab, and point coaching at the new thread — the takeover's own tail, run for a thread
   * the server took over. A different customer lands by navigation instead.
   */
  static async landOnTextThread(userId: number, botConversationId: number): Promise<void> {
    await ContactCenterV3Model.loadConversationDetail(undefined, userId, undefined, { silent: true });
    ContactCenterV3Model.setSelectedChannel("sms");
    void ContactCenterV3Model.loadCoachingState(botConversationId);
    void ContactCenterV3Model.refreshFacility(botConversationId);
  }

  static endVoiceSession(): void {
    ContactCenterV3Model.setState({ activeCallSid: null, voiceState: "idle", isMuted: false });
  }

  // ─── Assignment Toast ─────────────────────────────────────────────────

  // ─── Error Handling ────────────────────────────────────────────────────

  /**
   * CC errors surface as the STANDARD studio toast (Chuck, 2026-08-18) — the
   * old full-width red banner across the page top is gone.
   *
   * Uses react-toastify's own card, icon, close button and progress bar. A
   * stale global rule in `Toast.scss` used to strip all of that, which is why
   * this once drew its own shell; removing the rule restored it here and for
   * every other plain-string toast in studio.
   */
  static showError(message: string): void {
    toast.error(message, {
      position: "bottom-right",
      autoClose: CC_TOAST_DURATION_MS,
      closeOnClick: true,
      pauseOnHover: true
    });
  }

  // ─── Assignment Toast ─────────────────────────────────────────────────

  static showAssignmentToast(toast: {
    assignmentId: number;
    userName: string;
    channel: string;
    reason?: string;
    expiresAt?: string;
    userId?: number;
    botConversationId?: number;
  }): void {
    ContactCenterV3Model.setState({ pendingAssignmentToast: toast });
  }

  static dismissAssignmentToast(): void {
    ContactCenterV3Model.setState({ pendingAssignmentToast: null });
  }

  // ─── Close / Reopen + Closed tab (ENG-2791/2792, plan §8.1) ───────────────

  static setListTab(tab: "open" | "closed"): void {
    ContactCenterV3Model.setState({ listTab: tab });
    if (tab === "closed") void ContactCenterV3Model.loadClosedConversations();
  }

  static async loadClosedConversations(): Promise<void> {
    const orgId = Member.orgId;
    const memberId = Member.id;
    if (!orgId || !memberId) return;
    const seq = ++ContactCenterV3Model.listRequestSeq;
    try {
      const response = await API.ContactCenterV3.getConversations(orgId, memberId, "closed", ContactCenterV3Model.state.listSearch || undefined);
      if (seq !== ContactCenterV3Model.listRequestSeq) return; // a newer fetch superseded this one
      // Closed rows come back flat (no escalation grouping applies).
      ContactCenterV3Model.setState({
        closedConversations: [...response.myEscalations, ...response.otherConversations],
        listLoadFailed: false
      });
    } catch (e) {
      warn("ContactCenterV3Model.loadClosedConversations failed", e);
      if (seq !== ContactCenterV3Model.listRequestSeq) return;
      ContactCenterV3Model.setState({ listLoadFailed: true });
    }
  }

  /**
   * Agent-close: archives the USER (ENG-3036; they move to the Closed tab and
   * reopen when they write again). Returns success so the CALLER can clear
   * the selection by navigating to the base route — selection is URL-driven
   * (ConversationsPage's route-sync effect instantly re-selects from the URL,
   * so a store-side selectUser(null) here silently gets undone).
   */
  static async closeConversation(userId: number): Promise<boolean> {
    const orgId = Member.orgId;
    if (!orgId) return false;
    try {
      await API.ContactCenterV3.closeUserConversations(orgId, userId);
      // Remove from the open lists immediately; the Closed tab refetches on entry.
      ContactCenterV3Model.setState({
        myEscalations: ContactCenterV3Model.state.myEscalations.filter((i) => i.userId !== userId),
        conversations: ContactCenterV3Model.state.conversations.filter((i) => i.userId !== userId),
        // Closed = archived (Chuck, 2026-08-17): land on the Open tab.
        listTab: "open"
      });
      return true;
    } catch (e) {
      warn("ContactCenterV3Model.closeConversation failed", e);
      // Surface the server's reason when it gives one — e.g. the takeover
      // guard: "End the takeover first."
      const message = e instanceof Error && e.message && !e.message.startsWith("HTTP error") ? e.message : "Failed to close the conversation";
      ContactCenterV3Model.showError(message);
      return false;
    }
  }

  /**
   * Reopen (un-archive) a user — the closed view's "Open" button. Opening
   * means ENGAGING (Chuck, 2026-08-17): land on the Open tab with the user's
   * profile selected — the asymmetric twin of Close, which deselects.
   */
  static async reopenConversation(userId: number): Promise<void> {
    const orgId = Member.orgId;
    if (!orgId) return;
    try {
      await API.ContactCenterV3.reopenUserConversations(orgId, userId);
      ContactCenterV3Model.setState({
        closedConversations: ContactCenterV3Model.state.closedConversations.filter((i) => i.userId !== userId),
        listTab: "open"
      });
      void ContactCenterV3Model.loadConversations();
      if (ContactCenterV3Model.state.selectedUserId === userId)
        void ContactCenterV3Model.loadConversationDetail(undefined, undefined, undefined, { silent: true });
    } catch (e) {
      warn("ContactCenterV3Model.reopenConversation failed", e);
      ContactCenterV3Model.showError("Failed to reopen the conversation");
    }
  }

  // ─── Recovery (ENG-2826) ────────────────────────────────────────────────

  /** Missed-offer ids already surfaced this session — a resync must not re-show them. */
  private static seenMissedOfferIds = new Set<number>();

  /**
   * Re-deliver escalation offers that arrived (or expired) while this agent
   * wasn't listening. Live offers re-raise the toast with the server
   * deadline; expired ones surface as "You missed this one" notices.
   */
  static async fetchPendingOffers(): Promise<void> {
    const orgId = Member.orgId;
    const memberId = Member.id;
    if (!orgId || !memberId) return;

    try {
      const { offers, missed } = await API.ContactCenterV3.getPendingOffers(orgId, memberId);

      const current = ContactCenterV3Model.state.pendingAssignmentToast;
      const live = offers.find((o) => new Date(o.expiresAt).getTime() > Date.now());
      if (live && current?.assignmentId !== live.assignmentId) {
        ContactCenterV3Model.showAssignmentToast({
          assignmentId: live.assignmentId,
          userName: live.userName,
          channel: live.channel,
          reason: live.escalationReason,
          expiresAt: live.expiresAt,
          userId: live.userId,
          botConversationId: live.botConversationId
        });
      }

      const fresh = missed.filter((m) => !ContactCenterV3Model.seenMissedOfferIds.has(m.assignmentId));
      if (fresh.length > 0) {
        for (const m of fresh) ContactCenterV3Model.seenMissedOfferIds.add(m.assignmentId);
        ContactCenterV3Model.setState({ missedOfferNotices: [...ContactCenterV3Model.state.missedOfferNotices, ...fresh] });
      }
    } catch (e) {
      warn("ContactCenterV3Model.fetchPendingOffers failed", e);
    }
  }

  private static nextCcNoticeId = 1;

  /**
   * A success notice confirms and goes away on its own after CC_TOAST_DURATION_MS (the "Text
   * sent" card otherwise sat until dismissed, 2026-11-02); an error notice stays until the agent
   * dismisses it, so a refusal is never missed.
   */
  static showCCNotice(kind: "success" | "error", text: string): void {
    const id = ContactCenterV3Model.nextCcNoticeId++;
    ContactCenterV3Model.setState({
      ccNotices: [...ContactCenterV3Model.state.ccNotices, { id, kind, text }]
    });
    if (kind === "success") setTimeout(() => ContactCenterV3Model.dismissCCNotice(id), CC_TOAST_DURATION_MS);
  }

  static dismissCCNotice(id: number): void {
    ContactCenterV3Model.setState({ ccNotices: ContactCenterV3Model.state.ccNotices.filter((n) => n.id !== id) });
  }

  static showPresenceNotice(message: string): void {
    ContactCenterV3Model.setState({ presenceNotice: message });
  }

  static dismissPresenceNotice(): void {
    ContactCenterV3Model.setState({ presenceNotice: null });
  }

  static dismissMissedOffer(assignmentId: number): void {
    ContactCenterV3Model.setState({
      missedOfferNotices: ContactCenterV3Model.state.missedOfferNotices.filter((m) => m.assignmentId !== assignmentId)
    });
  }

  /**
   * The agent watched this offer's countdown reach zero on screen — it wasn't
   * "missed", it was declined by inaction in full view. Pre-seed the dedup set
   * so the next resync doesn't re-raise it as a "You missed this one" notice.
   */
  static markOfferExpiryWitnessed(assignmentId: number): void {
    ContactCenterV3Model.seenMissedOfferIds.add(assignmentId);
  }

  /**
   * Patch the conversation list in place from a `new_message` event (R7,
   * ENG-2827) — the event payload carries everything the row needs, so a full
   * org-wide list refetch per message would be pure waste (it stampedes at
   * scale and adds latency). Returns false when the conversation isn't in the
   * list yet (brand-new conversation) — the caller falls back to a refetch.
   */
  static patchListItemNewMessage(payload: {
    botConversationId: number;
    userId: number;
    lastMessageContent?: string;
    lastMessageAt?: string;
    latestMessageIndex?: number;
    role?: string;
  }): boolean {
    const { myEscalations, conversations, conversationDetail } = ContactCenterV3Model.state;

    // Unread increments only for USER messages, and not while the agent has
    // this conversation open + on screen (that read is stamped server-side).
    const isOpenAndVisible =
      typeof document !== "undefined" &&
      !document.hidden &&
      (conversationDetail?.channels.some((c) => c.botConversationId === payload.botConversationId) ?? false);
    const bumpUnread = payload.role === "user" && !isOpenAndVisible;
    // The row PREVIEW shows only the user's last message (v2 parity —
    // ENG-2946): bot/agent sends must not overwrite it. They still bump
    // recency/ordering and the message index below — matching the list load,
    // where the preview query filters role='user' but ranking uses any role.
    const previewContent = payload.role === "user" ? payload.lastMessageContent : undefined;

    let found = false;
    const patchList = (list: CCConversationListItem[]): CCConversationListItem[] => {
      const patched = list.map((item) => {
        if (item.userId !== payload.userId || !item.channels.some((ch) => ch.botConversationId === payload.botConversationId)) return item;
        found = true;
        return {
          ...item,
          lastActiveAt: payload.lastMessageAt ?? item.lastActiveAt,
          channels: item.channels.map((ch) =>
            ch.botConversationId === payload.botConversationId
              ? {
                  ...ch,
                  lastMessageContent: previewContent ?? ch.lastMessageContent,
                  lastMessageAt: payload.lastMessageAt ?? ch.lastMessageAt,
                  latestMessageIndex: Math.max(ch.latestMessageIndex ?? -1, payload.latestMessageIndex ?? -1),
                  unreadCount: bumpUnread ? (ch.unreadCount ?? 0) + 1 : ch.unreadCount
                }
              : ch
          )
        };
      });
      // Most-recent-first ordering matches the server sort.
      return found ? patched.sort((a, b) => b.lastActiveAt.localeCompare(a.lastActiveAt)) : patched;
    };

    const nextEscalations = patchList(myEscalations);
    const nextConversations = patchList(conversations);

    // A user message UN-ARCHIVES the user server-side (ENG-3036) — drop them
    // from the client's closed list immediately so the closed banner and the
    // Closed tab don't show a user who is open again.
    const closed = ContactCenterV3Model.state.closedConversations;
    if (payload.role === "user" && closed.some((i) => i.userId === payload.userId)) {
      ContactCenterV3Model.setState({ closedConversations: closed.filter((i) => i.userId !== payload.userId) });
    }

    if (!found) return false;

    ContactCenterV3Model.setState({ myEscalations: nextEscalations, conversations: nextConversations });
    return true;
  }

  /** Mark a conversation's list row read locally (open/visible) — the server stamp happens via markRead. */
  static clearListUnread(botConversationId: number): void {
    const clear = (list: CCConversationListItem[]): CCConversationListItem[] =>
      list.map((item) =>
        item.channels.some((ch) => ch.botConversationId === botConversationId)
          ? { ...item, channels: item.channels.map((ch) => (ch.botConversationId === botConversationId ? { ...ch, unreadCount: 0 } : ch)) }
          : item
      );
    ContactCenterV3Model.setState({
      myEscalations: clear(ContactCenterV3Model.state.myEscalations),
      conversations: clear(ContactCenterV3Model.state.conversations)
    });
  }

  /** Refresh the agent-presence map from the server (reads-only snapshot). */
  static async refreshAgentPresence(): Promise<void> {
    const orgId = Member.orgId;
    if (!orgId) return;
    try {
      const rows = await API.ContactCenterV3.getOrgPresence(orgId);
      const agentPresence: Record<number, { state: string; activeAssignmentCount: number }> = {};
      for (const r of rows) agentPresence[r.memberId] = { state: r.state, activeAssignmentCount: r.activeAssignmentCount };
      ContactCenterV3Model.setState({ agentPresence });
    } catch (e) {
      warn("ContactCenterV3Model.refreshAgentPresence failed", e);
    }
  }

  private static lastResyncAt = 0;

  /**
   * Full recovery pass (ENG-2826): everything a disconnected/backgrounded
   * client may have missed — the conversation list, pending/missed offers,
   * the presence snapshot, and the open conversation's rooms + messages.
   * Fired on bot-socket (re)connect, tab-visible, network-online, and
   * pageshow; throttled so clustered lifecycle events run it once.
   */
  static resyncAll(): void {
    const now = Date.now();
    if (now - ContactCenterV3Model.lastResyncAt < 5_000) return;
    ContactCenterV3Model.lastResyncAt = now;

    void ContactCenterV3Model.loadConversations();
    void ContactCenterV3Model.fetchPendingOffers();
    void ContactCenterV3Model.refreshAgentPresence();
    ContactCenterV3Model.resyncOpenConversation();
  }
}
